import type { ColDef } from "ag-grid-community";

function getColumnId<T>(col: ColDef<T>): string {
  return (col.colId ?? col.field) as string;
}

export interface BuildColumnsForPreferencesParams<TData> {
  /**
   * Always-present column definitions, e.g. record-level static fields.
   */
  staticColumns: ColDef<TData>[];

  /**
   * Column definitions sourced from the server (e.g. per-org metadata
   * keys, custom fields).
   */
  dynamicColumns: ColDef<TData>[];

  /**
   * Ordered list of column ids from the user's stored prefs snapshot.
   */
  preferenceOrder: readonly string[];
}

/**
 * Compose a consumer's column definitions in preference-driven order.
 */
export function buildColumnsForPreferences<TData>({
  staticColumns,
  dynamicColumns,
  preferenceOrder,
}: BuildColumnsForPreferencesParams<TData>): ColDef<TData>[] {
  // `Map` iteration is insertion order (ES2015 spec). `byId.set(id, …)`
  // on an existing key keeps the original position and replaces the
  // value — that's how preference-ordered stubs survive being overlaid with
  // a static or dynamic ColDef without moving.
  const byId = new Map<string, ColDef<TData>>();
  const fromPreferences = new Set(preferenceOrder);

  for (const id of preferenceOrder) {
    if (!byId.has(id)) {
      byId.set(id, { colId: id, hide: false });
    }
  }

  for (const col of staticColumns) {
    const id = getColumnId(col);
    byId.set(id, fromPreferences.has(id) ? { ...col, hide: false } : col);
  }

  for (const col of dynamicColumns) {
    const id = getColumnId(col);
    if (fromPreferences.has(id)) {
      byId.set(id, { ...col, hide: false });
    } else if (!byId.has(id)) {
      byId.set(id, col);
    }
  }

  return Array.from(byId.values());
}
